import React, { useEffect, useState, useMemo } from 'react';
import { usePatient } from '../../../../Contexts/PatientContext';
import styles from './style.module.css';
import LeftIcon from '../leftIcon.svg';
import HealthCustomCard from '../../../../Components/HealthCustomCard';
import ProfileImage from '../images/profile.svg';
import * as moment from 'moment';
import Modal from '../../../../Components/Modal';
import CustomDatePicker from '../../../../Components/DatePicker';
import { createTreatment, updateProduct, upsertCustomer } from '../../../../services/kollectaHealthCore';
import Papa from 'papaparse';
import Select from 'react-select';
import modalRed from '../redModalIcon.svg';
import modalGreen from '../greenModalIcon.svg';
import editIcon from './edit.svg';

const Profile = ({ onAddTreatment }) => {
  const { patientData, addTreatment, updatePatient, refreshPatientData } = usePatient();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [genderModalOpen, setGenderModalOpen] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [drugList, setDrugList] = useState([]);
  const [editData, setEditData] = useState({
    productId: patientData?.id,
    purpose: patientData?.treatments?.length ? patientData.treatments[patientData.treatments.length - 1]?.purpose : null,
    previousSystemicOncoTherapy: patientData?.treatments?.length ? patientData.treatments[patientData.treatments.length - 1]?.previousSystemicOncoTherapy : null,
    previousOncoSurgery: patientData?.treatments?.length ? patientData.treatments[patientData.treatments.length - 1]?.previousOncoSurgery : null,
    previousRadiationTherapy: patientData?.treatments?.length ? patientData.treatments[patientData.treatments.length - 1]?.previousRadiationTherapy : null,
    currentProtocol: patientData?.treatments?.length ? patientData.treatments[patientData.treatments.length - 1]?.currentProtocol : null,
    gender: patientData?.customer?.gender || '',
    status: patientData?.status || 'imported',
  });
  const [localPatientData, setLocalPatientData] = useState(patientData);

  const statusLabels = {
    'imported': 'Ativo',
    'obito_por_cancer': 'Óbito por câncer',
    'obito_por_outra_causa': 'Óbito por outra causa',
    'obito_desconhecido': 'Óbito desconhecido',
    'saida_do_estudo_por_opcao': 'Saída do estudo por opção',
    'erro_no_cadastro': 'ERRO NO CADASTRO',
    'dor_controlada': 'DOR CONTROLADA',
  };

  const purposeLabels = {
    'adjuvante': 'Adjuvante',
    'neoadjuvante': 'Neoadjuvante',
    'opaliativo': 'Paliativo',
  };

  const handleEdit = (item) => {
    setEditData(item);
    setEditModalOpen(true);
  };

  const handleEditGender = () => {
    setGenderModalOpen(true);
  };

  const handleSaveGender = async () => {
    try {
      const updatedData = {
        personalNumber: patientData?.customer?.personalNumber,
        name: patientData?.customer?.name,
        email: patientData?.customer?.email,
        phone: patientData?.customer?.phone,
        gender: editData.gender,
      };

      await upsertCustomer(updatedData);
      updatePatient({ ...patientData, customer: { ...patientData.customer, gender: editData.gender } });
      setSuccessModal(true);
    } catch (error) {
      setErrorModal(true);
      console.error('Erro ao atualizar o gênero:', error);
    } finally {
      setGenderModalOpen(false);
    }
  };

  const handleConfirmStatusSave = async () => {
    try {
      await updateProduct(patientData.id, { status: editData.status });

      setLocalPatientData((prev) => ({
        ...prev,
        status: editData.status,
      }));

      console.log('Status do tratamento atualizado com sucesso!');
      setSuccessModal(true);
    } catch (error) {
      console.error('Erro ao atualizar o status do tratamento:', error);
      setErrorModal(true);
    } finally {
      setEditModalOpen(false);
    }
  };

  const handleConfirmSave = async () => {
    try {
      const response = await createTreatment(editData);
      addTreatment(response.data);
      
      const updatedPatientData = {
        ...patientData,
        treatments: [...(patientData.treatments || []), response.data]
      };
      setLocalPatientData(updatedPatientData);
      
      updatePatient(updatedPatientData);
      
      try {
        await refreshPatientData();
      } catch (refreshError) {
        console.error('Erro ao atualizar dados do paciente:', refreshError);
      }
      
      console.log('Tratamento atualizado com sucesso!');
      setConfirmModalOpen(false);
      setSuccessModal(true);
    } catch (error) {
      setErrorModal(true);
      console.error('Erro ao atualizar o tratamento:', error);
    }
  };

  useEffect(() => {
    if (patientData && patientData.id) {
      setEditData((prev) => ({ ...prev, productId: patientData.id }));
      setLocalPatientData({ ...patientData });
    }
  }, [patientData]);
  
  const treatmentsKey = patientData?.treatments?.map(t => 
    `${t.id}-${t.treatmentMedications?.length || 0}`
  ).join('|') || '';
  
  useEffect(() => {
    if (patientData && treatmentsKey) {
      setLocalPatientData({ ...patientData });
    }
  }, [treatmentsKey, patientData]);

  const drugOptions = drugList.map((drug) => ({ label: drug, value: drug }));

  const handleDrugChange = (selectedOptions) => {
    const selectedDrugs = selectedOptions.map((option) => option.value);
    setEditData({ ...editData, currentProtocol: selectedDrugs });
  };

  useEffect(() => {
    if (editModalOpen) {
      window.scrollTo(0, 0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [editModalOpen]);

  useEffect(() => {
    fetch('/drogas.csv')
      .then((response) => response.text())
      .then((csvData) => {
        Papa.parse(csvData, {
          header: true,
          complete: (results) => {
            const allDrugNames = results.data.map((item) => Object.values(item)[0]);
            setDrugList(allDrugNames);
          },
        });
      });
  }, []);

  useEffect(() => {
    if (confirmModalOpen) {
      window.scrollTo(0, 0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [confirmModalOpen]);

  useEffect(() => {
    const lastTreatment = patientData?.treatments?.[patientData.treatments.length - 1] || {};

    setEditData((prev) => ({
      ...prev,
      productId: patientData?.id,
      purpose: lastTreatment?.purpose || '',
      previousSystemicOncoTherapy: lastTreatment?.previousSystemicOncoTherapy || false,
      previousOncoSurgery: lastTreatment?.previousOncoSurgery || false,
      previousRadiationTherapy: lastTreatment?.previousRadiationTherapy || false,
      currentProtocol: lastTreatment?.currentProtocol ? lastTreatment.currentProtocol.map((item) => item) : [],
      status: patientData?.status || 'imported',
      startDate: lastTreatment?.startDate ? new Date(lastTreatment.startDate) : new Date(),
    }));
  }, [patientData]);

  return (
    <>
      <HealthCustomCard>
        <div className={styles.profileCard}>
          <div className={styles.profileCardLeft}>
            <div className={styles.profileCardLeftTop}>
              <img src={ProfileImage} alt="Kollecta Health" />
              <h1>{patientData?.customer?.name}</h1>
            </div>

            <div className={styles.profileCardLeftInfos}>
              <div className={styles.profileCardLeftContent}>
                <p>
                  <span>CPF</span>
                  {patientData?.customer?.personalNumber}
                </p>

                <p>
                  <span>Data de Nascimento</span>
                  {patientData?.customer?.birthday ? moment(patientData.customer.birthday).format('DD/MM/YYYY') : '-'}
                </p>
              </div>

              <div className={styles.profileCardLeftContent}>
                <p>
                  <span>Celular</span>
                  {patientData?.customer?.phone}
                </p>

                <p className={styles.gender}>
                  <div className={styles.genderContent}>
                    <span>Gênero</span>
                    {patientData?.customer?.gender && patientData?.customer?.gender[0]}
                  </div>
                  <img
                    src={editIcon}
                    alt="Edit"
                    className={styles.editIcon}
                    onClick={handleEditGender}
                  />
                </p>
              </div>
            </div>
          </div>

          <div className={styles.profileCardRight}>
            <div className={styles.subtitle}>
              <h3> Informações do Estudo </h3>
              <img src={LeftIcon} alt="Kollecta Health" />
            </div>

            <div className={styles.profileCardRightContent}>
              <p>
                <span>Data do cadastro no sistema</span>
                {patientData?.createdAt ? moment(patientData.createdAt).format('DD/MM/YYYY') : '-'}
              </p>
            </div>

            <div
              className={`${localPatientData?.status === 'imported' ? styles.statusActive : styles.statusInactive}`}
            >
              <p>
                <span>Condição do Tratamento</span>

                <div className={styles.editContainer}>
                  {statusLabels[localPatientData?.status] || '-'}{' '}
                  <img
                    src={editIcon}
                    alt="Edit"
                    className={styles.editIcon}
                    onClick={() => handleEdit(localPatientData)}
                  />
                </div>
              </p>
            </div>

            <div className={styles.profileCardRightContent}>
              <p>
                <span>Tratamento Atual</span>
                {useMemo(() => {
                  const lastTreatment = patientData?.treatments?.[patientData.treatments?.length - 1];
                  if (lastTreatment?.treatmentMedications?.length > 0) {
                    const sortedMedications = [...lastTreatment.treatmentMedications].sort((a, b) => {
                      if (a.createdAt && b.createdAt) {
                        return new Date(b.createdAt) - new Date(a.createdAt);
                      }
                      if (a.created_at && b.created_at) {
                        return new Date(b.created_at) - new Date(a.created_at);
                      }
                      return 0;
                    });
                    
                    const latestMedication = sortedMedications[0];
                    const medicationName = latestMedication.medication?.name || latestMedication.medicationName || 'Nome não encontrado';
                    return medicationName;
                  } else if (lastTreatment?.currentProtocol?.length > 0) {
                    return Array.isArray(lastTreatment.currentProtocol) 
                      ? lastTreatment.currentProtocol.join(', ')
                      : lastTreatment.currentProtocol;
                  }
                  return '-';
                }, [patientData?.treatments?.length, patientData?.treatments?.[patientData?.treatments?.length - 1]?.treatmentMedications?.length])}
              </p>
            </div>

            {onAddTreatment && (
              <div className={styles.profileCardRightContent}>
                <button onClick={onAddTreatment} className={styles.generalButton}>
                  Alterar Tratamento
                </button>
              </div>
            )}
          </div>
        </div>
      </HealthCustomCard>

      {genderModalOpen && (
        <Modal onClose={() => setGenderModalOpen(false)}>
          <div className={styles.containerModal}>
            <h2>Editar Gênero</h2>
            <select
              value={editData.gender}
              onChange={(e) => setEditData({ ...editData, gender: e.target.value })}
              className={styles.selectStyle}
            >
              <option value="Masculino">Masculino</option>
              <option value="Feminino">Feminino</option>
            </select>
            <div className={styles.buttonsContainer}>
              <button onClick={handleSaveGender} className={styles.buttonSuccess}>
                Salvar
              </button>
              <button onClick={() => setGenderModalOpen(false)} className={styles.buttonError}>
                Cancelar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {confirmModalOpen && (
        <Modal onClose={() => setConfirmModalOpen(false)}>
          <div className={styles.containerModal}>
            <div className={styles.subtitle}>
              <h3> Adicionar Novo Tratamento</h3>
              <img src={LeftIcon} alt="Kollecta Health" />
            </div>

            <div className={styles.modalItem}>
              <label> Início do Novo Tratamento </label>
              <CustomDatePicker
                value={new Date(editData.treatmentDate)}
                onDateChange={(date) => setEditData({ ...editData, startDate: date })}
              />
            </div>

            <div className={styles.modalItem}>
              <label> Finalidade do tratamento </label>
              <select
                value={editData.purpose}
                onChange={(e) => setEditData({ ...editData, purpose: e.target.value })}
                className={styles.selectStyle}
              >
                {Object.entries(purposeLabels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.modalItem}>
              <label> Nova medicação </label>
              <Select
                isMulti
                options={drugOptions}
                onChange={handleDrugChange}
                className="basic-multi-select"
                classNamePrefix="select"
              />
            </div>

            <div className={styles.buttonsContainer}>
              <button onClick={handleConfirmSave} className={styles.buttonSuccess}>
                Alterar
              </button>
              <button onClick={() => setConfirmModalOpen(false)} className={styles.buttonError}>
                Cancelar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {successModal && (
        <Modal onClose={() => setSuccessModal(false)}>
          <div className={styles.containerModal}>
            <img src={modalGreen} alt="Green Modal Icon" />
            <h2>Alteração realizada!</h2>
          </div>
        </Modal>
      )}

      {errorModal && (
        <Modal onClose={() => setErrorModal(false)}>
          <div className={styles.containerModal}>
            <img src={modalRed} alt="Red Modal Icon" />
            <h2>Erro ao adicionar tratamento!</h2>
            <p> Tente novamente </p>
          </div>
        </Modal>
      )}

      {editModalOpen && (
        <Modal onClose={() => setEditModalOpen(false)}>
          <div className={styles.containerModal}>
            <h2>Editar Condição do Tratamento</h2>
            <select
              value={editData.status}
              onChange={(e) => setEditData({ ...editData, status: e.target.value })}
              className={styles.selectStyle}
            >
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <div className={styles.buttonsContainer}>
              <button onClick={handleConfirmStatusSave} className={styles.buttonSuccess}>
                Salvar
              </button>
              <button onClick={() => setEditModalOpen(false)} className={styles.buttonError}>
                Cancelar
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Profile;
